import React, { useCallback, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import importIcon from "assets/images/svg/import.svg";
import exportIcon from "assets/images/svg/export.svg";
import { STO_TYPES } from "pages/stock-management/stockTransferOrders/constants";
import { textFormatter } from "components/Common/formatter";
import STODetailsModal from "pages/stock-management/stockTransferOrders/STODetails/STODetailsModal";
import CustomLink from "components/Common/buttons/CustomLink";
import { Collapse, Divider } from "antd";
import NGIDropdownButton from "components/Common/buttons/NGIDropdownButton";
import menuIcon from "assets/images/svg/MenuVertical.svg";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { retrieveTripAssets, updateTripSTO } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import { userHasOneOfRoles } from "utils/helpers/functions";
import { USER_ROLES } from "utils/constants";
import classnames from "classnames";
import EditSTOModal from "pages/stock-management/trips/editTrip/editSTO/EditSTOModal";
import ConfirmationUnassignModal from "pages/stock-management/trips/editTrip/editSTO/ConfirmationUnassignModal";
import NGITable from "components/Common/NGITable";
import { assetsColumns } from "pages/stock-management/trips/tripDetails/constants";

const useStyles = makeStyles({
  assetItem: {
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: "#f5f5f5",
    borderRadius: 50,
    padding: 9,
    paddingRight: 30,
  },
  changed: {
    backgroundColor: "#ecfaf0",

    "&> .ant-collapse-header": {
      backgroundColor: "#ecfaf0",
      borderRadius: "30px!important",
    },
    "&> .ant-collapse-content": {
      backgroundColor: "#ecfaf0!important",
    },
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    marginRight: 20,
    background: "rgba(255, 255, 255, 1)",
  },
  subtitle: {
    marginRight: 10,
    color: "grey",
  },
  rightContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "50%",
    marginRight: 10,
  },
  outbound: {
    marginLeft: 3,
  },
  inbound: {
    marginRight: 3,
  },
  displayFlex: {
    display: "flex",
  },
  link: {
    fontWeight: 600,
    textDecoration: "underline",
  },
  collapse: {
    marginBottom: 10,
    border: "none",
    borderRadius: "30px",
    backgroundColor: "#f5f5f5",
    "&> .ant-collapse-item:last-child > .ant-collapse-header": {
      borderRadius: "0px 0px 30px 30px",
    },
    "&> .ant-collapse-item": {
      borderBottom: "none",
    },
  },
  collapsePanel: {
    borderRadius: "30px!important",

    "&> .ant-collapse-header": {
      padding: "0 !important",
      borderBottom: "none",
    },
    "&> .ant-collapse-content": {
      backgroundColor: "#f5f5f5",
      borderRadius: "0px 0px 30px 30px !important",
    },
  },
  description: {
    display: "flex",
    justifyContent: "center",
  },
  viewAll: {
    height: 49,
    display: "flex",
    alignItems: "center",
    marginRight: 13,
    textDecoration: "underline",
  },
  extraContainer: {
    display: "flex",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    background: "rgba(255, 255, 255, 1)",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
});

const { Panel } = Collapse;

const AssetItem = ({ asset, stop, isChanged = false, isEdit, tripStatus, update, tripNumber, setChangedList }) => {
  const classes = useStyles();
  const { stoType, group, condition, quantity, stoId, stoItemId } = asset;
  const [isLoading, setLoader] = useState(false);
  const [loadingState, setLoadingState] = useState({
    offload: false,
    load: false,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpenType, setIsEditOpenType] = useState("");
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isInboundRemoveOpen, setIsInboundRemoveOpen] = useState(false);
  const [loadedAssets, setLoadedAssets] = useState([]);
  const [offloadedAssets, setOffloadedAssets] = useState([]);

  const openDetails = useCallback(e => {
    e.stopPropagation();
    setIsOpen(true);
  }, []);

  const closeDetails = useCallback(() => setIsOpen(false), []);

  const openEditSto = useCallback(isReCreate => setIsEditOpenType(isReCreate ? "isReCreate" : "new"), []);
  const closeEditSto = useCallback(() => setIsEditOpenType(""), []);

  const openRemoveSto = useCallback(() => {
    if (tripStatus === "Pending" || tripStatus === "Planned") {
      if (stoType === STO_TYPES.INBOUND) {
        setIsInboundRemoveOpen(true);
        return;
      }
      if (stoType === STO_TYPES.OUTBOUND) {
        setIsRemoveOpen(true);
      }
    }
  }, [stoType]);
  const closeRemoveSto = useCallback(() => setIsRemoveOpen(false), []);
  const closeRemoveInboundSto = useCallback(() => setIsInboundRemoveOpen(false), []);

  const handleRemove = useCallback(() => {
    setLoader(true);
    updateTripSTO(
      {
        stoItemId,
        stopId: stop.id,
        isStoItemUnassigned: true,
      },
      tripNumber
    )
      .then(() => {
        closeRemoveSto();
        setLoader(false);
        successMessage("STO was unassigned successfully");
        update();
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [tripNumber, stop, stoItemId]);

  const loadAssets = useCallback(list => {
    if (list.length) {
      getLoadAssets();
      getOffLoadAssets();
    }
  }, []);

  const getLoadAssets = useCallback((filterOptions = {}) => {
    setLoadingState(prevState => ({ ...prevState, load: true }));
    retrieveTripAssets(tripNumber, {
      depot: stop.location.depot,
      listType: "load",
      assetsGroup: group,
      assetsCondition: condition,
      ...filterOptions,
    }).then(res => {
      setLoadingState(prevState => ({ ...prevState, load: false }));
      setLoadedAssets(res?.data || []);
    });
  }, []);

  const getOffLoadAssets = useCallback((filterOptions = {}) => {
    setLoadingState(prevState => ({ ...prevState, offload: true }));
    retrieveTripAssets(tripNumber, {
      depot: stop.location.depot,
      listType: "offload",
      assetsGroup: group,
      assetsCondition: condition,
      ...filterOptions,
    }).then(res => {
      setLoadingState(prevState => ({ ...prevState, offload: false }));
      setOffloadedAssets(res?.data || []);
    });
  }, []);

  const handleRemoveInbound = useCallback(
    reason => {
      closeRemoveInboundSto();
      switch (reason) {
        case "unassign":
          setLoader(true);
          updateTripSTO(
            {
              stoItemId,
              stopId: stop.id,
              isStoItemUnassigned: true,
            },
            tripNumber
          )
            .then(() => {
              closeRemoveSto();
              setLoader(false);
              successMessage("STO was unassigned successfully");
              update();
            })
            .catch(err => {
              setLoader(false);
              defaultNgiErrorMethod(err);
            });
          break;
        case "createNew":
          openEditSto(true);
          break;
        default:
          break;
      }
    },
    [tripNumber, stop, stoItemId]
  );
  const columns = useMemo(() => assetsColumns(), []);

  return (
    <>
      <Collapse onChange={loadAssets} className={classes.collapse} collapsible expandIconPosition={"end"}>
        <Panel
          key={"panel"}
          className={classnames(classes.collapsePanel, isChanged && classes.changed)}
          extra={
            <div className={classes.extraContainer}>
              <div className={classes.viewAll}>View all assets</div>
              {isEdit &&
                userHasOneOfRoles([
                  USER_ROLES.SYSTEM_FULL_ADMINS,
                  USER_ROLES.PRODUCT_TECHNICIAN_LEAD,
                  USER_ROLES.IT_OPERATIONS,
                  USER_ROLES.IT_OPERATIONS_LEADERSHIP,
                  USER_ROLES.WAREHOUSE,
                  USER_ROLES.FINANCE,
                ]) && (
                  <NGIDropdownButton
                    toggleEl={
                      <div className={classes.actions}>
                        <img src={menuIcon} alt="remove" />
                      </div>
                    }
                    menuItems={[
                      {
                        label: "Edit",
                        callback: openEditSto,
                      },
                      {
                        label: "Unassign Order",
                        disabled: !["Pending", "Planned"].includes(tripStatus),
                        callback: openRemoveSto,
                      },
                    ]}
                  />
                )}
            </div>
          }
          showArrow={false}
          header={
            <div className={classnames(classes.assetItem, isChanged && classes.changed)}>
              <div className={classes.iconContainer}>
                <div className={classes.icon}>
                  <img
                    className={stoType === STO_TYPES.INBOUND ? classes.inbound : classes.outbound}
                    src={stoType === "Inbound" ? importIcon : exportIcon}
                    alt="sto type"
                  />
                </div>
                <div>
                  <CustomLink className={classes.link} onClick={openDetails}>
                    {`${group} (${stoId})`}
                  </CustomLink>
                </div>
              </div>
              <div className={classes.rightContainer}>
                <div className={classes.displayFlex}>
                  <span className={classes.subtitle}>Condition</span>
                  <b>{textFormatter(condition)}</b>
                </div>
                <div className={classes.displayFlex}>
                  <span className={classes.subtitle}>QTY</span>
                  <b>{textFormatter(quantity)}</b>
                </div>
              </div>
            </div>
          }
          style={{ padding: 0, margin: 0 }}
        >
          <h6>Loaded assets ({loadedAssets?.result?.meta?.totalCount || 0}):</h6>
          <NGITable isLoading={loadingState.load} columns={columns} data={loadedAssets} updateList={getLoadAssets} />

          <Divider />

          <h6>Offloaded assets ({offloadedAssets?.result?.meta?.totalCount || 0}):</h6>
          <NGITable
            isLoading={loadingState.offload}
            columns={columns}
            data={offloadedAssets}
            updateList={getOffLoadAssets}
          />
        </Panel>
      </Collapse>
      {isOpen && <STODetailsModal id={stoId} onClose={closeDetails} />}
      {!!isEditOpenType && (
        <EditSTOModal
          createType={isEditOpenType}
          tripStatus={tripStatus}
          stop={stop}
          setChangedList={setChangedList}
          editId={stoItemId}
          onClose={closeEditSto}
          id={stoId}
          update={update}
          isEditTrip={isEdit}
          tripNumber={tripNumber}
        />
      )}

      {isRemoveOpen && (
        <ConfirmationModal
          loading={isLoading}
          title="Confirm Unassigning STO"
          description="Are you sure you want to unassign this STO?"
          confirmBtnTitle="Confirm"
          cancelBtnTitle="Cancel"
          onCancel={closeRemoveSto}
          onConfirm={handleRemove}
        />
      )}
      {isInboundRemoveOpen && (
        <ConfirmationUnassignModal
          loading={isLoading}
          onCancel={closeRemoveInboundSto}
          onConfirm={handleRemoveInbound}
        />
      )}
    </>
  );
};

export default AssetItem;
